<template>
  <div class="network-file-services">
    <div class="header">
      <h2>{{ $t('networkFile.title') }}</h2>
      <div class="actions">
        <el-button type="info" size="small" @click="showLogDialog">
          <el-icon><Document /></el-icon>
          {{ $t('networkFile.viewLogs') }}
        </el-button>
      </div>
    </div>

    <!-- 服务控制卡片 -->
    <div class="services-row">
      <!-- WebDAV文件服务 (/tfdav - 复用端口) -->
      <div class="service-card">
        <h3>{{ $t('networkFile.webdav') }}</h3>
        <div class="service-controls">
          <span class="status-badge" :class="{ 'running': webdavSwitch }">
            {{ $t(webdavSwitch ? 'networkFile.running' : 'networkFile.stopped') }}
          </span>
          <button
            @click="toggleWebDAVService"
            :disabled="webdavServiceLoading"
            class="action-btn"
          >
            <span v-if="webdavServiceLoading">{{ $t('networkFile.processing') }}</span>
            <span v-else>{{ $t(webdavSwitch ? 'networkFile.stop' : 'networkFile.start') }}</span>
          </button>
        </div>
      </div>

      <!-- HTTPS WebDAV文件服务 -->
      <div class="service-card">
        <h3>{{ $t('networkFile.webdavHttps') }}</h3>
        <div class="service-controls">
          <div class="port-input-group">
            <span>{{ $t('networkFile.port') }}:</span>
            <input
              type="number"
              v-model.number="webdavHttpsPort"
              class="port-input"
              :disabled="webdavHttpsRunning"
              min="1"
              max="65535"
            />
            <button
              v-if="!webdavHttpsRunning"
              @click="saveWebdavHttpsPort"
              class="save-port-btn"
            >
              {{ $t('networkFile.save') }}
            </button>
          </div>
          <span class="status-badge" :class="{ 'running': webdavHttpsRunning }">
            {{ $t(webdavHttpsRunning ? 'networkFile.running' : 'networkFile.stopped') }}
          </span>
          <button
            @click="toggleWebDAVHttps"
            :disabled="webdavServiceLoading"
            class="action-btn"
          >
            <span v-if="webdavServiceLoading">{{ $t('networkFile.processing') }}</span>
            <span v-else>{{ $t(webdavHttpsRunning ? 'networkFile.stop' : 'networkFile.start') }}</span>
          </button>
        </div>
      </div>

      <!-- HTTP WebDAV文件服务 -->
      <div class="service-card">
        <h3>{{ $t('networkFile.webdavHttp') }}</h3>
        <div class="service-controls">
          <div class="port-input-group">
            <span>{{ $t('networkFile.port') }}:</span>
            <input
              type="number"
              v-model.number="webdavHttpPort"
              class="port-input"
              :disabled="webdavHttpRunning"
              min="1"
              max="65535"
            />
            <button
              v-if="!webdavHttpRunning"
              @click="saveWebdavHttpPort"
              class="save-port-btn"
            >
              {{ $t('networkFile.save') }}
            </button>
          </div>
          <span class="status-badge" :class="{ 'running': webdavHttpRunning }">
            {{ $t(webdavHttpRunning ? 'networkFile.running' : 'networkFile.stopped') }}
          </span>
          <button
            @click="toggleWebDAVHttp"
            :disabled="webdavServiceLoading"
            class="action-btn"
          >
            <span v-if="webdavServiceLoading">{{ $t('networkFile.processing') }}</span>
            <span v-else>{{ $t(webdavHttpRunning ? 'networkFile.stop' : 'networkFile.start') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- SMB 服务 -->
    <div class="services-row">
      <div class="service-card">
        <h3>{{ $t('networkFile.smb') }}</h3>
        <div class="service-controls">
          <div class="port-input-group">
            <span>{{ $t('networkFile.port') }}:</span>
            <input
              type="number"
              v-model.number="smbPort"
              class="port-input"
              :disabled="smbRunning"
              min="1"
              max="65535"
            />
            <button
              v-if="!smbRunning"
              @click="saveSmbConfig"
              class="save-port-btn"
            >
              {{ $t('networkFile.save') }}
            </button>
          </div>
          <div class="config-group">
            <span>{{ $t('networkFile.workgroup') }}:</span>
            <input
              type="text"
              v-model="smbWorkgroup"
              class="text-input"
              :disabled="smbRunning"
              placeholder="WORKGROUP"
            />
          </div>
          <span class="status-badge" :class="{ 'running': smbRunning }">
            {{ $t(smbRunning ? 'networkFile.running' : 'networkFile.stopped') }}
          </span>
          <button
            @click="toggleSmbService"
            :disabled="smbServiceLoading"
            class="action-btn"
          >
            <span v-if="smbServiceLoading">{{ $t('networkFile.processing') }}</span>
            <span v-else>{{ $t(smbRunning ? 'networkFile.stop' : 'networkFile.start') }}</span>
          </button>
        </div>
      </div>
<!--
      <div class="service-card reserved">
        <h3>{{ $t('networkFile.sftp') }}</h3>
        <p class="reserved-hint">{{ $t('networkFile.sftpComing') }}</p>
      </div>
-->
    </div>

    <!-- 日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('networkFile.logTitle')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('networkFile.totalLogs', { count: logTotal }) }}</span>
          <div class="log-module-selector">
            <el-select v-model="logModule" size="small" :placeholder="$t('networkFile.selectModule')" @change="handleModuleChange">
              <el-option :label="$t('networkFile.webdavModule')" value="file" />
              <el-option :label="$t('networkFile.smbModule')" value="smb" />
            </el-select>
          </div>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('networkFile.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('networkFile.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
          >
            {{ $t('networkFile.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
          >
            {{ $t('networkFile.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            {{ $t('networkFile.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <el-icon><InfoFilled /></el-icon>
          <p>{{ $t('networkFile.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('networkFile.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { API_BASE_URL } from '@/config';
import { ElMessage, ElMessageBox } from 'element-plus';
import { InfoFilled, Document } from '@element-plus/icons-vue';

export default {
  name: 'NetworkFileServices',
  components: {
    InfoFilled,
    Document
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter();

    // 状态变量
    const webdavSwitch = ref(false);
    const webdavServiceLoading = ref(false);

    // WebDAV HTTPS/HTTP 独立配置
    const webdavHttpsPort = ref(8900);
    const webdavHttpsSwitch = ref(false);
    const webdavHttpsRunning = ref(false);
    const webdavHttpPort = ref(8910);
    const webdavHttpSwitch = ref(false);
    const webdavHttpRunning = ref(false);

    // SMB 服务配置
    const smbPort = ref(445);
    const smbWorkgroup = ref('WORKGROUP');
    const smbSwitch = ref(false);
    const smbRunning = ref(false);
    const smbServiceLoading = ref(false);

    // 日志相关数据
    const isMobile = window.innerWidth < 768;
    const logDialogVisible = ref(false);
    const logLoading = ref(false);
    const logClearing = ref(false);
    const logDownloading = ref(false);
    const logsData = ref([]);
    const logTotal = ref(0);
    const logCurrentPage = ref(1);
    const logPageSize = ref(50);
    const autoRefreshEnabled = ref(true);
    const logRefreshInterval = ref(null);
    const logModule = ref('file');
    const logContentRef = ref(null);

    // 检查登录状态
    const checkLogin = (data) => {
      if (data?.errCode === 1000) {
        ElMessage.error(t('common.notLoggedIn'));
        router.push('/user-login?callback=network-file-services');
        return false;
      }
      return true;
    };

    // 获取 /tfdav WebDAV 状态
    const fetchTFdavStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/list-fileMgmtUser`, {
          method: 'GET',
          credentials: 'include'
        });
        const data = await response.json();
        if (checkLogin(data)) {
          webdavSwitch.value = data.webdavSwitch || false;
        }
      } catch (error) {
        console.error(t('networkFile.fetchTFdavFailed'), error);
      }
    };

    // 获取 WebDAV HTTPS/HTTP 独立配置
    const fetchWebdavConfig = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get-webdav`, {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.errCode === 0) {
          webdavHttpsPort.value = data.port || 8900;
          webdavHttpsSwitch.value = data.flag || false;
          webdavHttpsRunning.value = data.status || false;
          webdavHttpPort.value = data.port2 || 8910;
          webdavHttpSwitch.value = data.flag2 || false;
          webdavHttpRunning.value = data.status2 || false;
        }
      } catch (error) {
        console.error(t('networkFile.fetchWebdavConfigFailed'), error);
      }
    };

    // 切换 /tfdav WebDAV 服务 (复用端口)
    const toggleWebDAVService = async () => {
      webdavServiceLoading.value = true;
      try {
        const url = webdavSwitch.value
          ? `${API_BASE_URL}/stop-tfdav`
          : `${API_BASE_URL}/start-tfdav`;

        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.errMsg || t('networkFile.operationFailed'));
        }

        if (data.errCode === 1000) {
          ElMessage.error(t('common.notLoggedIn'));
          router.push('/user-login?callback=network-file-services');
          return;
        }

        if (data.errCode === 0) {
          webdavSwitch.value = !webdavSwitch.value;
          ElMessage.success(t(webdavSwitch.value ? 'networkFile.serviceStarted' : 'networkFile.serviceStopped'));
          await fetchTFdavStatus();
        } else {
          throw new Error(data.errMsg || t('networkFile.operationFailed'));
        }
      } catch (error) {
        console.error(t('networkFile.toggleWebdavError'), error);
        ElMessage.error(t('networkFile.operationFailed') + `: ${error.message}`);
      } finally {
        webdavServiceLoading.value = false;
      }
    };

    // 保存 HTTPS 端口
    const saveWebdavHttpsPort = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/update-webdav`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `httpsPort=${webdavHttpsPort.value}&httpsFlag=${webdavHttpsSwitch.value}`
        });
        const data = await response.json();
        if (data.errCode === 0) {
          ElMessage.success(t('networkFile.httpsPortSaved'));
        } else {
          throw new Error(data.errMsg || t('networkFile.saveFailed'));
        }
      } catch (error) {
        console.error(t('networkFile.saveHttpsPortFailed'), error);
        ElMessage.error(t('networkFile.saveFailed') + `: ${error.message}`);
      }
    };

    // 保存 HTTP 端口
    const saveWebdavHttpPort = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/update-webdav`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `httpPort=${webdavHttpPort.value}&httpFlag=${webdavHttpSwitch.value}`
        });
        const data = await response.json();
        if (data.errCode === 0) {
          ElMessage.success(t('networkFile.httpPortSaved'));
        } else {
          throw new Error(data.errMsg || t('networkFile.saveFailed'));
        }
      } catch (error) {
        console.error(t('networkFile.saveHttpPortFailed'), error);
        ElMessage.error(t('networkFile.saveFailed') + `: ${error.message}`);
      }
    };

    // 切换 HTTPS WebDAV
    const toggleWebDAVHttps = async () => {
      webdavServiceLoading.value = true;
      try {
        const url = webdavHttpsRunning.value
          ? `${API_BASE_URL}/stop-webdav`
          : `${API_BASE_URL}/start-webdav`;
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `mode=https&port=${webdavHttpsPort.value}`
        });
        const data = await response.json();
        if (data.errCode === 0) {
          ElMessage.success(t('networkFile.webdavHttps') + ` ${webdavHttpsRunning.value ? t('networkFile.stopped') : t('networkFile.running')}`);
          await fetchWebdavConfig();
        } else {
          throw new Error(data.errMsg || t('networkFile.operationFailed'));
        }
      } catch (error) {
        console.error(t('networkFile.toggleHttpsWebdavError'), error);
        ElMessage.error(t('networkFile.operationFailed') + `: ${error.message}`);
      } finally {
        webdavServiceLoading.value = false;
      }
    };

    // 切换 HTTP WebDAV
    const toggleWebDAVHttp = async () => {
      webdavServiceLoading.value = true;
      try {
        const url = webdavHttpRunning.value
          ? `${API_BASE_URL}/stop-webdav`
          : `${API_BASE_URL}/start-webdav`;
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `mode=http&port=${webdavHttpPort.value}`
        });
        const data = await response.json();
        if (data.errCode === 0) {
          ElMessage.success(t('networkFile.webdavHttp') + ` ${webdavHttpRunning.value ? t('networkFile.stopped') : t('networkFile.running')}`);
          await fetchWebdavConfig();
        } else {
          throw new Error(data.errMsg || t('networkFile.operationFailed'));
        }
      } catch (error) {
        console.error(t('networkFile.toggleHttpWebdavError'), error);
        ElMessage.error(t('networkFile.operationFailed') + `: ${error.message}`);
      } finally {
        webdavServiceLoading.value = false;
      }
    };

    // 获取 SMB 配置
    const fetchSmbConfig = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get-smb`, {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.errCode === 0) {
          smbPort.value = data.port || 445;
          smbWorkgroup.value = data.workgroup || 'WORKGROUP';
          smbSwitch.value = data.flag || false;
          smbRunning.value = data.status || false;
        }
      } catch (error) {
        console.error(t('networkFile.fetchSmbConfigFailed'), error);
      }
    };

    // 保存 SMB 配置
    const saveSmbConfig = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/update-smb`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `port=${smbPort.value}&workgroup=${smbWorkgroup.value}`
        });
        const data = await response.json();
        if (data.errCode === 0) {
          ElMessage.success(t('networkFile.configSaved'));
        } else {
          throw new Error(data.errMsg || t('networkFile.saveFailed'));
        }
      } catch (error) {
        console.error(t('networkFile.saveSmbConfigFailed'), error);
        ElMessage.error(t('networkFile.saveFailed') + `: ${error.message}`);
      }
    };

    // 切换 SMB 服务
    const toggleSmbService = async () => {
      smbServiceLoading.value = true;
      try {
        const url = smbRunning.value
          ? `${API_BASE_URL}/stop-smb`
          : `${API_BASE_URL}/start-smb`;
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `port=${smbPort.value}&workgroup=${smbWorkgroup.value}`
        });
        const data = await response.json();
        if (data.errCode === 0) {
          ElMessage.success(t(smbRunning.value ? 'networkFile.serviceStopped' : 'networkFile.serviceStarted'));
          await fetchSmbConfig();
        } else {
          throw new Error(data.errMsg || t('networkFile.operationFailed'));
        }
      } catch (error) {
        console.error(t('networkFile.toggleSmbError'), error);
        ElMessage.error(t('networkFile.operationFailed') + `: ${error.message}`);
      } finally {
        smbServiceLoading.value = false;
      }
    };

    // 显示日志对话框
    const showLogDialog = () => {
      logDialogVisible.value = true;
      fetchLogs();
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh();
      }
    };

    // 切换自动刷新
    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh();
        ElMessage.success(t('common.autoRefreshEnabled'));
      } else {
        stopLogAutoRefresh();
        ElMessage.info(t('common.autoRefreshDisabled'));
      }
    };

    // 启动日志自动刷新
    const startLogAutoRefresh = () => {
      stopLogAutoRefresh();
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false);
        }
      }, 5000);
    };

    // 停止日志自动刷新
    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value);
        logRefreshInterval.value = null;
      }
    };

    // 获取日志
    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true;
        }

        const params = new URLSearchParams({
          module: logModule.value,
          page: logCurrentPage.value,
          size: logPageSize.value
        });

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });

        if (!response.ok) throw new Error(t('common.networkError'));

        const data = await response.json();
        if (data.errCode === 1000) {
          ElMessage.error(t('common.notLoggedIn'));
          router.push('/user-login?callback=network-file-services');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('common.fetchLogsFailed'));
        }

        logsData.value = data.data || [];
        logTotal.value = data.count || 0;

        if (showLoading && logContentRef.value) {
          logContentRef.value.scrollTop = 0;
        }
      } catch (error) {
        console.error(t('common.fetchLogsFailed'), error);
        if (showLoading) {
          ElMessage.error(t('common.fetchLogsFailed') + `: ${error.message || t('common.unknownError')}`);
        }
      } finally {
        if (showLoading) {
          logLoading.value = false;
        }
      }
    };

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs(true);
    };

    // 清空日志
    const clearLogs = async () => {
      try {
        await ElMessageBox.confirm(t('networkFile.confirmClearLogs'), t('common.warning'), {
          confirmButtonText: t('common.confirm'),
          cancelButtonText: t('common.cancel'),
          type: 'warning'
        });

        logClearing.value = true;

        const params = new URLSearchParams({
          module: logModule.value
        });

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (!response.ok) throw new Error(t('common.networkError'));

        const data = await response.json();
        if (data.errCode === 0) {
          ElMessage.success(t('common.logsCleared'));
          fetchLogs(true);
        } else {
          throw new Error(data.errMsg || t('common.clearLogsFailed'));
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error(t('common.clearLogsFailed'), error);
          ElMessage.error(t('common.clearLogsFailed') + `: ${error.message || t('common.unknownError')}`);
        }
      } finally {
        logClearing.value = false;
      }
    };

    // 下载所有日志
    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true;

        const params = new URLSearchParams({
          module: logModule.value,
          page: 1,
          size: 10000
        });

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });

        if (!response.ok) throw new Error(t('common.networkError'));

        const data = await response.json();
        if (data.errCode === 0) {
          const allLogs = data.data || [];

          if (allLogs.length === 0) {
            ElMessage.warning(t('common.noLogsToDownload'));
            return;
          }

          let logContent = '';
          allLogs.forEach(log => {
            logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`;
          });

          const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${logModule.value}_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          ElMessage.success(t('common.downloadSuccess', { count: allLogs.length }));
        } else {
          throw new Error(data.errMsg || t('common.fetchLogsFailed'));
        }
      } catch (error) {
        console.error(t('common.downloadLogsFailed'), error);
        ElMessage.error(t('common.downloadLogsFailed') + `: ${error.message || t('common.unknownError')}`);
      } finally {
        logDownloading.value = false;
      }
    };

    // 处理模块变化
    const handleModuleChange = () => {
      logCurrentPage.value = 1;
      fetchLogs(true);
    };

    // 处理每页条数变化
    const handleLogSizeChange = (size) => {
      logPageSize.value = size;
      logCurrentPage.value = 1;
      fetchLogs(true);
    };

    // 处理页码变化
    const handleLogPageChange = (page) => {
      logCurrentPage.value = page;
      fetchLogs(true);
    };

    onMounted(() => {
      fetchTFdavStatus();
      fetchWebdavConfig();
      fetchSmbConfig();
    });

    return {
      isMobile,
      webdavSwitch,
      webdavServiceLoading,
      webdavHttpsPort,
      webdavHttpsSwitch,
      webdavHttpsRunning,
      webdavHttpPort,
      webdavHttpSwitch,
      webdavHttpRunning,
      toggleWebDAVService,
      saveWebdavHttpsPort,
      saveWebdavHttpPort,
      toggleWebDAVHttps,
      toggleWebDAVHttp,
      smbPort,
      smbWorkgroup,
      smbSwitch,
      smbRunning,
      smbServiceLoading,
      saveSmbConfig,
      toggleSmbService,
      logDialogVisible,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      autoRefreshEnabled,
      logModule,
      showLogDialog,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleModuleChange,
      handleLogSizeChange,
      handleLogPageChange,
      logContentRef
    };
  }
};
</script>

<style scoped>
.network-file-services {
  padding: 20px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  min-height: calc(100vh - 40px);
  background-color: #f5f7fa;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  box-sizing: border-box;
}

.services-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  width: 100%;
}

@media (max-width: 768px) {
  .services-row {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
  }
}

.service-card {
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 15px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.service-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.service-card h3 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

@media (max-width: 768px) {
  .service-card {
    padding: 12px 16px;
    width: 100%;
  }

  .service-card h3 {
    font-size: 15px;
    margin-bottom: 8px;
  }
}

@media (max-width: 480px) {
  .service-card {
    padding: 10px 12px;
  }

  .service-card h3 {
    font-size: 14px;
  }
}

.service-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .service-controls {
    gap: 8px;
  }
}

.status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.status-badge.running {
  background: #e6f7e6;
  color: #4caf50;
}

.status-badge:not(.running) {
  background: #f0f0f0;
  color: #757575;
}

@media (max-width: 768px) {
  .status-badge {
    padding: 3px 8px;
    font-size: 11px;
  }
}

.action-btn {
  padding: 6px 12px;
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;
  min-width: 60px;
}

.action-btn:hover {
  background: #e0e0e0;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .action-btn {
    padding: 5px 10px;
    font-size: 12px;
    min-width: 50px;
  }
}

@media (max-width: 480px) {
  .action-btn {
    padding: 4px 8px;
    font-size: 11px;
  }
}

/* 端口输入组样式 */
.port-input-group {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-right: 10px;
}

.port-input {
  width: 70px;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
}

.port-input:disabled {
  background-color: #f5f5f5;
  color: #999;
}

.save-port-btn {
  padding: 4px 8px;
  background-color: #409EFF;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

.save-port-btn:hover {
  background-color: #66b1ff;
}

/* 配置组样式 */
.config-group {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-right: 10px;
}

.text-input {
  width: 100px;
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
}

.text-input:disabled {
  background-color: #f5f5f5;
  color: #999;
}

/* 预留服务样式 */
.reserved-services {
  margin-top: 20px;
}

.reserved {
  opacity: 0.6;
  background: #fafafa;
}

.reserved h3 {
  color: #999;
}

.reserved-hint {
  color: #bbb;
  font-size: 13px;
  margin: 5px 0 0 0;
}

@media (max-width: 768px) {
  .reserved-services {
    margin-top: 0;
  }
}

/* 头部样式 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header h2 {
  color: #303133;
  font-size: 20px;
  font-weight: 500;
  margin: 0;
}

@media (max-width: 768px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .actions {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
}

/* 日志对话框样式 */
.log-dialog {
  :deep(.el-dialog__body) {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.log-stats {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 13px;
  color: #606266;
}

.log-module-selector {
  padding-left: 16px;
  border-left: 1px solid #dcdfe6;
}

.log-module-selector :deep(.el-select) {
  width: 120px;
}

.auto-refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 16px;
  border-left: 1px solid #dcdfe6;
}

.refresh-label {
  font-size: 12px;
  color: #909399;
}

.refresh-interval {
  font-size: 12px;
  color: #67c23a;
}

.log-actions {
  display: flex;
  gap: 8px;
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.empty-log {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
}

.empty-log .el-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.empty-log p {
  margin: 0;
  font-size: 14px;
}

.log-list {
  padding: 12px;
}

.log-item {
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.log-item:last-child {
  border-bottom: none;
}

.log-item.log-level-error {
  background-color: #fef0f0;
}

.log-item.log-level-warn {
  background-color: #fdf6ec;
}

.log-time {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-level {
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-level.level-error {
  color: #f56c6c;
}

.log-level.level-warn {
  color: #e6a23c;
}

.log-level.level-info {
  color: #409eff;
}

.log-message {
  color: #303133;
  word-break: break-all;
  flex: 1;
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .log-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .log-stats {
    width: 100%;
    flex-wrap: wrap;
    gap: 12px;
  }

  .log-module-selector,
  .auto-refresh-control {
    padding-left: 0;
    border-left: none;
  }

  .log-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .log-content {
    max-height: 350px;
  }

  .log-list {
    padding: 8px;
  }

  .log-item {
    font-size: 12px;
    flex-wrap: wrap;
    gap: 4px;
  }

  .log-time {
    width: 100%;
    margin-bottom: 2px;
  }

  .log-message {
    width: 100%;
    margin-left: 0;
  }
}
</style>
